import { useState, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import AdminLayout from "../../../../components/layout/AdminLayout";
import { App } from "antd";
import {
  getQuestion,
  createQuestion,
  editQuestion,
} from "../../../../services/recommendedQuestion";

export default function EditRecommendedQuestionPage() {
  const navigate = useNavigate();
  const { id } = useParams();
  const { t } = useTranslation();
  const { message } = App.useApp();
  const isNew = id === "new";

  const [question, setQuestion] = useState("");
  const [isEnabled, setIsEnabled] = useState(false);
  const [loading, setLoading] = useState(!isNew);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");

  // 编辑模式：加载现有数据
  useEffect(() => {
    if (isNew || !id) return;

    getQuestion(id)
      .then((res) => {
        setQuestion(res.data.Entity.Question);
        setIsEnabled(res.data.Entity.IsEnabled);
      })
      .catch(() => {
        setError(t("common.noDataFound"));
      })
      .finally(() => setLoading(false));
  }, [id, isNew, t]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError("");
    setSubmitting(true);

    try {
      if (isNew) {
        await createQuestion({ Question: question, IsEnabled: isEnabled });
      } else {
        await editQuestion({
          ID: id,
          Question: question,
          IsEnabled: isEnabled,
        });
      }

      message.success(t("editQ.savedRedirecting"));
      setTimeout(() => navigate("/admin/recommended-questions"), 1500);
    } catch (err: unknown) {
      const data = (err as { response?: { data?: { message?: string } } })?.response?.data;
      setError(data?.message || t("rec.operationFailed"));
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <AdminLayout>
        <div className="py-20 text-center text-gray-400">
          <i className="ri-loader-4-line animate-spin text-2xl"></i>
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      <div className="max-w-2xl mx-auto">
        <div className="mb-6">
          <button
            onClick={() => navigate("/admin/recommended-questions")}
            className="flex items-center gap-2 text-sm text-gray-500 hover:text-gray-800 mb-4 cursor-pointer transition-colors"
          >
            <i className="ri-arrow-left-line"></i>
            {t("common.backToList")}
          </button>
          <h1 className="text-xl font-semibold text-gray-900">
            {isNew ? t("editQ.titleNew") : t("editQ.titleEdit")}
          </h1>
          <p className="text-sm text-gray-400 mt-0.5">
            {isNew ? t("editQ.subtitleNew") : `${t("editQ.subtitleEdit")} ${id}`}
          </p>
        </div>

        {/* 错误提示 */}
        {error && (
          <div className="mb-4 px-4 py-3 rounded-lg bg-red-50 border border-red-200 flex items-center gap-2">
            <i className="ri-error-warning-line text-red-500 text-base"></i>
            <span className="text-sm text-red-600">{error}</span>
          </div>
        )}

        <form onSubmit={handleSubmit} className="space-y-6">
          {/* 问题内容 */}
          <div className="bg-white border border-gray-200 rounded-lg p-5">
            <label className="block text-sm font-medium text-gray-800 mb-2">
              {t("editQ.questionContent")} <span className="text-red-500">*</span>
            </label>
            <textarea
              value={question}
              onChange={(e) => setQuestion(e.target.value)}
              placeholder={t("editQ.questionPlaceholder")}
              rows={3}
              required
              className="w-full px-4 py-3 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:border-transparent resize-none"
              style={{ "--tw-ring-color": "#830051" } as React.CSSProperties}
            />
            <div className="flex items-start gap-1.5 mt-2">
              <i className="ri-lightbulb-line text-[#E07B00] text-sm flex-shrink-0 mt-0.5"></i>
              <p className="text-xs text-gray-400">{t("editQ.questionHint")}</p>
            </div>
          </div>

          {/* 状态 */}
          <div className="bg-white border border-gray-200 rounded-lg p-5">
            <label className="block text-sm font-medium text-gray-800 mb-3">
              {t("editQ.statusLabel")}
            </label>
            <div className="flex gap-3">
              {([true, false] as const).map((enabled) => (
                <label
                  key={String(enabled)}
                  className="flex-1 flex items-center gap-3 p-3 border rounded-lg cursor-pointer transition-all"
                  style={
                    isEnabled === enabled
                      ? { borderColor: "#830051", backgroundColor: "#F8ECF3" }
                      : { borderColor: "#E5E7EB", backgroundColor: "#fff" }
                  }
                >
                  <input
                    type="radio"
                    name="status"
                    value={String(enabled)}
                    checked={isEnabled === enabled}
                    onChange={() => setIsEnabled(enabled)}
                    className="cursor-pointer"
                    style={{ accentColor: "#830051" }}
                  />
                  <div>
                    <div className="text-sm font-medium text-gray-900">
                      {enabled ? t("common.active") : t("common.inactive")}
                    </div>
                    <div className="text-xs text-gray-400 mt-0.5">
                      {enabled ? t("editQ.activeDesc") : t("editQ.inactiveDesc")}
                    </div>
                  </div>
                </label>
              ))}
            </div>
          </div>

          <div className="flex items-center justify-end gap-3">
            <button
              type="button"
              onClick={() => navigate("/admin/recommended-questions")}
              className="px-4 py-2 border border-gray-200 text-gray-600 rounded-lg text-sm font-medium hover:bg-gray-50 transition-colors whitespace-nowrap cursor-pointer"
            >
              {t("common.cancel")}
            </button>
            <button
              type="submit"
              disabled={!question || submitting}
              className="px-5 py-2 text-white rounded-lg text-sm font-medium transition-colors whitespace-nowrap disabled:opacity-50 disabled:cursor-not-allowed cursor-pointer flex items-center gap-2"
              style={{ backgroundColor: "#830051" }}
            >
              {submitting && <i className="ri-loader-4-line animate-spin text-sm"></i>}
              {isNew ? t("editQ.createQuestion") : t("editQ.saveChanges")}
            </button>
          </div>
        </form>
      </div>
    </AdminLayout>
  );
}
